import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GlobalVariableWidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import Icon, { ExclamationCircleFilled } from '@ant-design/icons';
import GlobalVariableWidgetProcessor from '@kemu-io/kemu-core/widgets/globalVariable/index.js';
import {
	ReadInputName,
	SetValueInputName,
	getDefaultState,
} from '@kemu-io/kemu-core/widgets/globalVariable/index.js';
import { DataType } from '@kemu-io/hs-types';
import {
  GateUI,
	GateUIProps,
	GateCustomSettingsProps,
  GetPortsInformationFunction
} from '../index.js';
import styles from './globalVariable.module.css';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import GateIcon from '@components/gateIcon/gateIcon';
import useTranslation, { getTranslationFunction } from '@hooks/useTranslation';
import { ReactComponent as GlobalVariableWidgetIcon } from '@src/components/gates/globalVariable/icon.svg';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import { Tooltip, Menu } from 'antd';
import { SETTINGS_CONTAINER_CLASS } from '@common/constants';
import classNames from 'classnames';
import globalVariablesManager, { parsePath } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import WidgetDropdownButton from '@components/WidgetDropdownButton/WidgetDropdownButton';
import kemuCore from '@kemu-io/kemu-core';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';

type Props = GateUIProps

const GlobalVariableWidget = (props: Props) => {
  const { recipeId, thingRecipeId, info, repaintExistingPorts } = props;
  const [state, setState] = useReactiveWidgetState<GlobalVariableWidgetState>(recipeId, thingRecipeId, info.id);
  const [varUndefined, setVarUndefined] = useState(false);
  const [globalVariables, setGlobalVariables] = useState(globalVariablesManager.getAllVariables());
  const t = useTranslation('Widget.GlobalVariable');

	const fixedState = {
		...getDefaultState(),
		...state
	};

  /**
   * Builds a hierarchical menu structure from flat variable list
   */
  const buildMenuStructure = useMemo(() => {
    const nonGroupVariables = globalVariables.filter(v => v.controlType !== 'group');
    
    // Group variables by their group path
    const groupedByPath: Record<string, typeof nonGroupVariables> = {};
    const ungrouped: typeof nonGroupVariables = [];
    
    // Collect all group paths (including parent paths) to build complete hierarchy
    const allGroupPaths = new Set<string>();
    
    for (const variable of nonGroupVariables) {
      const { group, name: displayName } = parsePath(variable.name);
      if (group) {
        if (!groupedByPath[group]) {
          groupedByPath[group] = [];
        }
        groupedByPath[group].push(variable);
        
        // Add all parent group paths to the set
        const pathParts = group.split('.');
        for (let i = 1; i <= pathParts.length; i++) {
          const parentPath = pathParts.slice(0, i).join('.');
          allGroupPaths.add(parentPath);
        }
      } else {
        ungrouped.push(variable);
      }
    }
    
    /**
     * Recursively builds menu items for a group path
     */
    const buildMenuItemsForGroup = (groupPath: string): React.ReactNode[] => {
      const items: React.ReactNode[] = [];
      
      // Find variables in this group
      const variablesInGroup = groupedByPath[groupPath] || [];
      for (const variable of variablesInGroup) {
        const { name: displayName } = parsePath(variable.name);
        items.push(
          <Menu.Item key={variable.name}>
            {displayName}
          </Menu.Item>
        );
      }
      
      // Find nested groups (groups that start with this group path + '.')
      // Check both groupedByPath and allGroupPaths to find nested groups
      const nestedGroups = Array.from(allGroupPaths).filter(path => {
        return path.startsWith(groupPath + '.') && path.split('.').length === groupPath.split('.').length + 1;
      });
      
      for (const nestedGroupPath of nestedGroups) {
        const { name: groupDisplayName } = parsePath(nestedGroupPath);
        items.push(
          <Menu.SubMenu key={nestedGroupPath} title={groupDisplayName}>
            {buildMenuItemsForGroup(nestedGroupPath)}
          </Menu.SubMenu>
        );
      }
      
      return items;
    };
    
    const menuItems: React.ReactNode[] = [];
    
    // Add ungrouped variables first
    for (const variable of ungrouped) {
      const { name: displayName } = parsePath(variable.name);
      menuItems.push(
        <Menu.Item key={variable.name}>
          {displayName}
        </Menu.Item>
      );
    }
    
    // Add top-level groups (from allGroupPaths, not just groupedByPath)
    const topLevelGroups = Array.from(allGroupPaths).filter(path => !path.includes('.'));
    for (const groupPath of topLevelGroups) {
      const { name: groupDisplayName } = parsePath(groupPath);
      menuItems.push(
        <Menu.SubMenu key={groupPath} title={groupDisplayName}>
          {buildMenuItemsForGroup(groupPath)}
        </Menu.SubMenu>
      );
    }
    
    return menuItems;
  }, [globalVariables]);

  const reinitializeWidget = useCallback(() => {
    kemuCore.initializeWidget({
      recipePoolId: props.recipeId,
      thingRecipeId: DEFAULT_THING_ID,
      widgetRecipeId: info.id,
    });
  }, [props.recipeId, info.id]);

  /**
   * Handles menu item selection
   */
  const handleMenuClick = useCallback((e: any) => {
    const selectedName = e.key as string;
    setState((s) => ({
      ...s,
      name: selectedName,
    }));
    setVarUndefined(false);
  }, [setState]);

  /**
   * Refreshes the global variables list
   */
  const refreshGlobalVariables = useCallback(() => {
    const globalVars = globalVariablesManager.getAllVariables();
    setGlobalVariables(globalVars);
    setVarUndefined(!globalVars.some(v => v.name === fixedState.name) && fixedState.name !== '');
  }, [fixedState.name]);

  const handleGlobalVariableRenamed = useCallback((config: { oldName: string, newName: string }) => {
    const { oldName, newName } = config;
    if (fixedState.name === oldName) {
      setState((s) => ({
        ...s,
        name: newName,
      }));
    }
  }, [fixedState.name]);

  // Subscribe to global variable changes
  useEffect(() => {
    const unsubscribeDefined = globalVariablesManager.onGlobalVariableDefined(refreshGlobalVariables);
    const unsubscribeRemoved = globalVariablesManager.onGlobalVariableRemoved(refreshGlobalVariables);
    const unsubscribeRenamed = globalVariablesManager.onGlobalVariableRenamed(fixedState.name, handleGlobalVariableRenamed);

    // Refresh list on load
    refreshGlobalVariables();

    return () => {
      unsubscribeDefined();
      unsubscribeRemoved();
      unsubscribeRenamed();
    };
  }, [refreshGlobalVariables, handleGlobalVariableRenamed, fixedState.name]);


  useEffect(() => {
    if (fixedState.name) {
      repaintExistingPorts();
      reinitializeWidget();
    }
  }, [fixedState.name, repaintExistingPorts, reinitializeWidget]);

  /**
   * Gets the display name from the full path for the dropdown button
   */
  const displayName = useMemo(() => {
    if (!fixedState.name) {
      return t('SelectPlaceholder', 'Select a variable');
    }
    const { name } = parsePath(fixedState.name);
    return name;
  }, [fixedState.name, t]);

  const menu = (
    <Menu onClick={handleMenuClick} selectedKeys={[fixedState.name || '']}>
      {buildMenuStructure.length === 0 ? (
        <Menu.Item key="no-variables" disabled>
          {t('NoVariablesAvailable')}
        </Menu.Item>
      ) : (
        buildMenuStructure
      )}
    </Menu>
  );

  return (
    <div className={styles.GateBody}>
      {varUndefined && (
        <div className={styles.ErrorContainer}>
          <Tooltip title={t('VariableUndefined')} placement="top">
            <ExclamationCircleFilled className={styles.ErrorIcon} />
          </Tooltip>
        </div>
      )}
      <WidgetDropdownButton
        widgetType="storage"
        disabled={props.info.disabled}
        name={displayName}
        menu={menu}
        wrapperClassName={classNames({ 'is-disabled': props.info.disabled }, styles.DropDown)}
        overlayClassName={styles.DropdownOverlay}
      />
    </div>
  );
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={GlobalVariableWidgetIcon} />}/>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<GlobalVariableWidgetState>(props.recipeId, props.blockId, props.gateInfo.id);
	const fixedState = {
		...getDefaultState(),
		...state
	};

	const t = useTranslation('LogicMaker.Gates.GlobalVariable.Settings');

	/**
	 * Handles reactive switch change and re-initializes the widget
	 */
	const handleSwitchChange = async (checked: boolean) => {
		setState({ ...fixedState, reactive: checked });
		
		// Re-initialize the widget to properly subscribe/unsubscribe from global variable changes
		await kemuCore.initializeWidget({
			recipePoolId: props.recipeId,
			thingRecipeId: DEFAULT_THING_ID,
			widgetRecipeId: props.gateInfo.id,
		});
	};

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}

			<div className={styles.SwitchContainer}>
				<label>{t('ReactiveSwitch', 'Auto-emit on variable change')}</label>
				<KemuSwitch
					size="small"
					checked={fixedState.reactive}
					onChange={handleSwitchChange}
				/>
			</div>
		</div>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo, intl) => {
	const t = getTranslationFunction(intl, 'LogicMaker.Gates.GlobalVariable.Port');
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = GlobalVariableWidgetProcessor.getOutputNames(state, portContext);
  
	return {
		inputs: [
			{
				name: ReadInputName,
				type: DataType.Anything,
				position: [0, 0.3, -1, 0],
				label: t('read', 'read'),
			},
			{
				name: SetValueInputName,
				type: DataType.Anything,
				position: [0, 0.7, -1, 0],
				label: t('setValue', 'set value'),
			}
		],
		outputs: [{
			name: outputNames[0].name,
			type: outputNames[0].type,
			position: [1, 0.5, 1, 0],
			label: outputNames[0].label,
		}],
	};
};

export default {
	getPortsInformation,
  hasTitle: true,
	BarIcon: GateBarIcon,
	Element: GlobalVariableWidget,
	CustomSettingsDialog: GateCustomSettings,
  getWidgetTitle: (intl) => intl.formatMessage({ id: 'Widget.GlobalVariable.WidgetTitle' }),
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'Widget.GlobalVariable.Title' }),
} as GateUI;
